/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Flex,
  Textarea,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useCreatePatient } from '../hooks/useCreatePatient';
import { useUpdatePatient } from '../hooks/useUpdatePatient';
import { useGetPatientById } from '../hooks/useGetPatientById';

function PatientManagementForm({ editId, onSuccess, setSelectedId }) {
  const id = editId?._id;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      mobile: '',
      email: '',
      age: '',
      previousClinicalHistory: '',
      active: true,
    },
  });

  const { data } = useGetPatientById(id);

  useEffect(() => {
    if (data?.data?.result) {
      reset({
        name: data.data.result.name,
        mobile: data.data.result.mobile,
        email: data.data.result.email,
        age: data.data.result.age,
        previousClinicalHistory: data.data.result.previousClinicalHistory,
        active: data.data.result.active,
      });
    }
  }, [data, reset]);

  const createMutation = useCreatePatient({
    onSuccess: () => {
      reset();
      onSuccess?.();
    },
  });

  const updateMutation = useUpdatePatient({
    onSuccess: () => {
      reset();
      setSelectedId(null);
      onSuccess?.();
    },
  });

  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate(
        { id, ...formData },
        {
          onSuccess: () => {
            reset();
            onSuccess?.();
          },
        },
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          reset();
          onSuccess?.();
        },
      });
    }
  };

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card
        w="100%"
        maxW="900px"
        rounded="xl"
        boxShadow="rgba(0, 0, 0, 0.35) 0px 5px 15px"
      >
        <CardBody>
          <form onSubmit={handleSubmit(submitHandler)}>
            {/* ROW 1 */}
            <Flex gap={4} mb={3} direction={{ base: 'column', md: 'row' }}>
              <FormControl isInvalid={errors.name}>
                <FormLabel>
                  Name{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter patient name"
                  {...register('name', { required: 'Name is required' })}
                />
                {errors.name && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.name.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.mobile}>
                <FormLabel>
                  Mobile{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter mobile number"
                  {...register('mobile', { required: 'Mobile is required' })}
                />
                {errors.mobile && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.mobile.message}
                  </Text>
                )}
              </FormControl>
            </Flex>

            {/* ROW 2 */}
            <Flex gap={4} mb={3} direction={{ base: 'column', md: 'row' }}>
              <FormControl isInvalid={errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  placeholder="Enter email"
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Invalid email address',
                    },
                  })}
                />
                {errors.email && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.email.message}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={errors.age}>
                <FormLabel>
                  Age{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter age"
                  {...register('age', { required: 'Age is required' })}
                />
                {errors.age && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.age.message}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex
              gap={4}
              mb={3}
              mt={10}
              direction={{ base: 'column', md: 'row' }}
            >
              <FormControl isInvalid={errors.previousClinicalHistory}>
                <FormLabel>Previous Clinical History</FormLabel>
                <Textarea
                  placeholder="Enter Previous Clinical History"
                  rows={4}
                  resize="vertical"
                  {...register('previousClinicalHistory', {})}
                />
                {errors.previousClinicalHistory && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.previousClinicalHistory.message}
                  </Text>
                )}
              </FormControl>
            </Flex>

            <Flex justify="center" mt={16}>
              <Button
                type="submit"
                colorScheme="teal"
                isLoading={isSubmitting}
                w="160px"
              >
                {id ? 'Update Patient' : 'Register Patient'}
              </Button>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default PatientManagementForm;
